/**
 * Advanced search functionality using Fuse.js
 */

import Fuse from 'fuse.js';
import type { ContentItem, ContentType, Locale, ContentIndex } from './loader';

export interface SearchableContent {
  id: string;
  type: ContentType;
  title: string;
  excerpt: string;
  content: string;
  tags: string[];
  category?: string;
  date: string;
  locale: Locale;
  url: string;
  author?: string;
  customer?: string;
  publication?: string;
}

export interface SearchResult {
  item: SearchableContent;
  score: number;
  matches?: Fuse.FuseResultMatch[];
}

export interface SearchOptions {
  types?: ContentType[];
  categories?: string[];
  tags?: string[];
  dateRange?: {
    start?: string;
    end?: string;
  };
  limit?: number;
}

// Fuse.js configuration for optimal search results
const fuseOptions: Fuse.IFuseOptions<SearchableContent> = {
  keys: [
    { name: 'title', weight: 0.4 },
    { name: 'excerpt', weight: 0.3 },
    { name: 'content', weight: 0.2 },
    { name: 'tags', weight: 0.1 },
    { name: 'category', weight: 0.05 },
    { name: 'author', weight: 0.05 },
    { name: 'customer', weight: 0.05 },
    { name: 'publication', weight: 0.05 }
  ],
  threshold: 0.4, // Lower = more strict matching
  distance: 100,
  minMatchCharLength: 2,
  includeScore: true,
  includeMatches: true,
  ignoreLocation: true,
  findAllMatches: true
};

// Cache for search indices per locale
const searchIndices = new Map<Locale, Fuse<SearchableContent>>();
const searchableContent = new Map<Locale, SearchableContent[]>();

/**
 * Convert content index to searchable format
 */
function createSearchableContent(contentIndex: ContentIndex, locale: Locale): SearchableContent[] {
  const searchable: SearchableContent[] = [];
  
  // Process blog posts
  contentIndex.blog.forEach(post => {
    searchable.push({
      id: `blog-${post.slug}`,
      type: 'blog',
      title: post.title,
      excerpt: post.excerpt,
      content: post.content || '',
      tags: post.tags,
      category: post.category,
      date: post.date,
      locale,
      url: `/${locale === 'en' ? '' : locale + '/'}blog/${post.slug}`,
      author: post.author
    });
  });
  
  // Process changelog entries
  contentIndex.changelog.forEach(entry => {
    searchable.push({
      id: `changelog-${entry.slug}`,
      type: 'changelog',
      title: entry.title,
      excerpt: entry.excerpt,
      content: entry.content || '',
      tags: entry.tags,
      category: entry.category,
      date: entry.date,
      locale,
      url: `/${locale === 'en' ? '' : locale + '/'}changelog/${entry.slug}`
    });
  });
  
  // Process press articles
  contentIndex.press.forEach(article => {
    searchable.push({
      id: `press-${article.slug}`,
      type: 'press',
      title: article.title,
      excerpt: article.excerpt,
      content: article.content || '',
      tags: [],
      date: article.date,
      locale,
      url: article.url, // External URL for press articles
      publication: article.publication
    });
  });
  
  // Process customer stories
  contentIndex.stories.forEach(story => {
    searchable.push({
      id: `stories-${story.slug}`,
      type: 'stories',
      title: story.title,
      excerpt: story.excerpt,
      content: story.content || '',
      tags: story.tags,
      date: story.date,
      locale,
      url: `/${locale === 'en' ? '' : locale + '/'}stories/${story.slug}`,
      customer: story.customer
    });
  });
  
  return searchable;
}

/**
 * Initialize search index for a locale
 */
export function initializeSearchIndex(contentIndex: ContentIndex, locale: Locale): void {
  const content = createSearchableContent(contentIndex, locale);
  const fuse = new Fuse(content, fuseOptions);
  
  searchIndices.set(locale, fuse);
  searchableContent.set(locale, content);
}

/**
 * Perform advanced search with filters
 */
export function searchContent(
  query: string,
  locale: Locale,
  options: SearchOptions = {}
): SearchResult[] {
  const fuse = searchIndices.get(locale);
  if (!fuse || !query.trim()) {
    return [];
  }
  
  // Perform fuzzy search
  const fuseResults = fuse.search(query, { limit: options.limit || 50 });
  
  // Convert to our search result format
  let results: SearchResult[] = fuseResults.map(result => ({
    item: result.item,
    score: result.score || 0,
    matches: result.matches
  }));
  
  // Apply additional filters
  if (options.types && options.types.length > 0) {
    results = results.filter(result => options.types!.includes(result.item.type));
  }
  
  if (options.categories && options.categories.length > 0) {
    results = results.filter(result => 
      result.item.category && options.categories!.includes(result.item.category)
    );
  }
  
  if (options.tags && options.tags.length > 0) {
    results = results.filter(result =>
      result.item.tags.some(tag => options.tags!.includes(tag))
    );
  }
  
  if (options.dateRange) {
    const { start, end } = options.dateRange;
    results = results.filter(result => {
      const itemDate = new Date(result.item.date);
      const startDate = start ? new Date(start) : null;
      const endDate = end ? new Date(end) : null;
      
      if (startDate && itemDate < startDate) return false;
      if (endDate && itemDate > endDate) return false;
      
      return true;
    });
  }
  
  // Apply final limit
  if (options.limit) {
    results = results.slice(0, options.limit);
  }
  
  return results;
}

/**
 * Get search suggestions based on partial query
 */
export function getSearchSuggestions(
  query: string,
  locale: Locale,
  limit: number = 5
): string[] {
  const content = searchableContent.get(locale);
  if (!content || query.length < 2) {
    return [];
  }
  
  const suggestions = new Set<string>();
  const queryLower = query.toLowerCase();
  
  // Extract suggestions from titles, tags, and categories
  content.forEach(item => {
    // Title suggestions
    if (item.title.toLowerCase().includes(queryLower)) {
      suggestions.add(item.title);
    }
    
    // Tag suggestions
    item.tags.forEach(tag => {
      if (tag.toLowerCase().includes(queryLower)) {
        suggestions.add(tag);
      }
    });
    
    // Category suggestions
    if (item.category && item.category.toLowerCase().includes(queryLower)) {
      suggestions.add(item.category);
    }
  });
  
  return Array.from(suggestions).slice(0, limit);
}

/**
 * Get popular search terms (mock implementation)
 */
export function getPopularSearchTerms(locale: Locale, limit: number = 10): string[] {
  // In a real implementation, this would come from analytics data
  const popularTerms: Record<Locale, string[]> = {
    'en': ['time tracking', 'productivity', 'features', 'integrations', 'API', 'mobile', 'teams', 'reporting', 'automation', 'pricing'],
    'sv-SE': ['tidsregistrering', 'produktivitet', 'funktioner', 'integrationer', 'API', 'mobil', 'team', 'rapporter', 'automation', 'priser'],
    'fr-FR': ['suivi du temps', 'productivité', 'fonctionnalités', 'intégrations', 'API', 'mobile', 'équipes', 'rapports', 'automation', 'tarifs'],
    'es-ES': ['seguimiento de tiempo', 'productividad', 'características', 'integraciones', 'API', 'móvil', 'equipos', 'informes', 'automatización', 'precios'],
    'de-DE': ['zeiterfassung', 'produktivität', 'funktionen', 'integrationen', 'API', 'mobil', 'teams', 'berichte', 'automatisierung', 'preise'],
    'ja-JP': ['時間追跡', '生産性', '機能', '統合', 'API', 'モバイル', 'チーム', 'レポート', '自動化', '価格'],
    'zh-CN': ['时间跟踪', '生产力', '功能', '集成', 'API', '移动', '团队', '报告', '自动化', '价格'],
    'it-IT': ['tracciamento tempo', 'produttività', 'funzionalità', 'integrazioni', 'API', 'mobile', 'team', 'report', 'automazione', 'prezzi'],
    'ko-KR': ['시간 추적', '생산성', '기능', '통합', 'API', '모바일', '팀', '보고서', '자동화', '가격'],
    'pt-BR': ['rastreamento de tempo', 'produtividade', 'recursos', 'integrações', 'API', 'móvel', 'equipes', 'relatórios', 'automação', 'preços'],
    'vi-VN': ['theo dõi thời gian', 'năng suất', 'tính năng', 'tích hợp', 'API', 'di động', 'nhóm', 'báo cáo', 'tự động hóa', 'giá cả'],
    'pl-PL': ['śledzenie czasu', 'produktywność', 'funkcje', 'integracje', 'API', 'mobilny', 'zespoły', 'raporty', 'automatyzacja', 'ceny'],
    'nl-NL': ['tijdregistratie', 'productiviteit', 'functies', 'integraties', 'API', 'mobiel', 'teams', 'rapporten', 'automatisering', 'prijzen'],
    'nb-NO': ['tidsregistrering', 'produktivitet', 'funksjoner', 'integrasjoner', 'API', 'mobil', 'team', 'rapporter', 'automatisering', 'priser'],
    'da-DK': ['tidsregistrering', 'produktivitet', 'funktioner', 'integrationer', 'API', 'mobil', 'teams', 'rapporter', 'automatisering', 'priser'],
    'fi-FI': ['ajan seuranta', 'tuottavuus', 'ominaisuudet', 'integraatiot', 'API', 'mobiili', 'tiimit', 'raportit', 'automaatio', 'hinnat'],
    'uk-UA': ['відстеження часу', 'продуктивність', 'функції', 'інтеграції', 'API', 'мобільний', 'команди', 'звіти', 'автоматизація', 'ціни']
  };
  
  return popularTerms[locale]?.slice(0, limit) || popularTerms['en'].slice(0, limit);
}

/**
 * Clear search cache
 */
export function clearSearchCache(): void {
  searchIndices.clear();
  searchableContent.clear();
}
